import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";

const stats = [
  { value: "18+", label: "精选游戏" },
  { value: "6", label: "游戏大类" },
  { value: "100%", label: "免下载试玩" },
  { value: "全端", label: "电脑手机均可" },
];

const values = [
  {
    icon: "ri-compass-3-line",
    title: "让寻找变得简单",
    desc: "用分类、筛选与搜索，把杂乱无章的游戏信息整理清楚，让玩家几秒钟就能找到想玩的游戏。",
  },
  {
    icon: "ri-heart-3-line",
    title: "把试玩门槛降到最低",
    desc: "坚持免下载、免安装的理念，打开网页即可体验，减少玩家在下载与注册上的等待成本。",
  },
  {
    icon: "ri-shield-check-line",
    title: "清爽、可信的浏览体验",
    desc: "简洁的界面与清晰的游戏信息，不堆砌无关内容，让每一次浏览都舒服、顺畅。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "合作与反馈邮箱", value: "support@webgameplay.example", href: "mailto:support@webgameplay.example" },
  { icon: "ri-customer-service-2-line", label: "客服时间", value: "每日 9:00 - 21:00" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "示例市 · 创意产业园 A 座 8 层" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 网页游戏直接玩",
    description:
      "了解网页游戏直接玩平台：我们专注收集与整理热门网页游戏，提供免下载的在线试玩与清晰的游戏信息，帮助玩家快速找到心仪的游戏。",
    keywords: "网页游戏直接玩,关于我们,在线试玩平台,免下载游戏",
  });

  return (
    <div className="mx-auto max-w-screen2xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <section className="relative mt-5 overflow-hidden rounded-lg bg-gradient-to-br from-primary-900 via-foreground-950 to-accent-900 px-6 py-12 md:px-12 md:py-16">
        <div className="relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-bold text-background-50 backdrop-blur">
            <i className="ri-gamepad-line"></i>
            关于我们
          </span>
          <h1 className="mt-4 text-3xl font-black leading-tight text-background-50 md:text-4xl">
            网页游戏直接玩
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-background-100 md:text-base">
            我们是一个专注网页游戏的在线试玩与信息平台。整理的每一款游戏都配有封面、类型标签、玩法介绍与试玩入口，
            希望让“想玩就玩”变得简单——不需要下载，不需要安装，打开网页就能体验。
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-50/20 bg-background-50/10 px-4 py-4 backdrop-blur">
                <p className="text-2xl font-black text-background-50">{item.value}</p>
                <p className="mt-1 text-xs text-background-200">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-black text-foreground-900 md:text-2xl">我们的理念</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 text-base font-bold text-foreground-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="text-xl font-black text-foreground-900 md:text-2xl">平台定位与发展方向</h2>
          <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
            <p>
              网页游戏直接玩面向喜欢快速浏览、试玩网页游戏的休闲玩家、竞技玩家与角色扮演类爱好者。
              平台围绕“快”展开：快速查找、快速了解游戏信息、快速开始试玩。
            </p>
            <p>
              目前站内游戏信息均为演示数据，用于展示平台的结构与交互方式。后续我们计划持续扩充游戏库，
              完善筛选维度，并在条件成熟时接入真实的游戏运行环境，让试玩从“演示”走向“真的能玩”。
            </p>
            <p>
              如果你有想玩的游戏类型，或希望某款游戏出现在平台上，欢迎通过下方邮箱或反馈入口告诉我们。
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-gamepad-line"></i>
              返回首页逛游戏
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-5 py-3 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-compass-3-line"></i>
              去搜索游戏
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="text-xl font-black text-foreground-900">联系我们</h2>
          <p className="mt-2 text-sm text-foreground-500">
            以下联系方式为演示信息，欢迎通过邮件与我们取得联系。
          </p>
          <ul className="mt-5 space-y-4">
            {contacts.map((item) => (
              <li key={item.label} className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-foreground-500">{item.label}</span>
                  {item.href ? (
                    <a href={item.href} className="block break-all text-sm font-medium text-foreground-900 transition hover:text-primary-600">
                      {item.value}
                    </a>
                  ) : (
                    <span className="block text-sm font-medium text-foreground-900">{item.value}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-md border border-secondary-200 bg-secondary-50 p-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-secondary-900">
              <i className="ri-feedback-line"></i>
              反馈入口说明
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-secondary-900/80">
              如遇游戏信息错误、分类建议或功能反馈，请发送邮件至
              <a href="mailto:support@webgameplay.example" className="mx-1 font-medium underline">
                support@webgameplay.example
              </a>
              ，邮件标题建议注明“反馈 + 问题类型”，我们会在 1 - 2 个工作日内查看并处理。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}